import React, { useCallback } from "react";

/** Which coordinate the divider tracks, and the cursor it shows while dragging. */
type Axis = "x" | "y";

export interface DividerDragCallbacks {
  onDragStart?: () => void;
  /** Receives the pointer's position along the tracked axis. */
  onDragMove?: (position: number) => void;
  onDragEnd?: () => void;
}

export interface DividerDragHandlers {
  onMouseDown: (e: React.MouseEvent<HTMLDivElement>) => void;
  onTouchStart: () => void;
}

/**
 * Mouse and touch drag plumbing shared by the vertical and horizontal dividers:
 * listeners live on `document` for the duration of the drag so the pointer can
 * leave the 3px handle without dropping it.
 */
export function useDividerDrag(
  axis: Axis,
  { onDragStart, onDragMove, onDragEnd }: DividerDragCallbacks,
): DividerDragHandlers {
  const readPoint = useCallback(
    (point: { clientX: number; clientY: number }) => (axis === "x" ? point.clientX : point.clientY),
    [axis],
  );

  const onMouseDown = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      e.preventDefault();
      document.body.style.cursor = axis === "x" ? "col-resize" : "row-resize";
      document.body.style.userSelect = "none";
      onDragStart?.();

      const handleMouseMove = (moveEvent: MouseEvent): void => onDragMove?.(readPoint(moveEvent));

      const handleMouseUp = (): void => {
        document.removeEventListener("mousemove", handleMouseMove);
        document.removeEventListener("mouseup", handleMouseUp);
        document.body.style.cursor = "default";
        document.body.style.userSelect = "auto";
        onDragEnd?.();
      };

      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("mouseup", handleMouseUp);
    },
    [axis, readPoint, onDragStart, onDragMove, onDragEnd],
  );

  const onTouchStart = useCallback(() => {
    onDragStart?.();

    const handleTouchMove = (moveEvent: TouchEvent): void => {
      moveEvent.preventDefault(); // Prevent page scroll during drag
      onDragMove?.(readPoint(moveEvent.touches[0]));
    };

    const cleanup = (): void => {
      document.removeEventListener("touchmove", handleTouchMove);
      document.removeEventListener("touchend", cleanup);
      document.removeEventListener("touchcancel", cleanup);
      onDragEnd?.();
    };

    document.addEventListener("touchmove", handleTouchMove, { passive: false });
    document.addEventListener("touchend", cleanup);
    document.addEventListener("touchcancel", cleanup);
  }, [readPoint, onDragStart, onDragMove, onDragEnd]);

  return { onMouseDown, onTouchStart };
}
